import { useState } from 'react';
import { deletionRequestsAPI } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from '@/components/ui/dialog';
import { Trash2, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';

export function RequestDeletionButton({ prospect, onSuccess }) {
    const [open, setOpen] = useState(false);
    const [motif, setMotif] = useState('');
    const [loading, setLoading] = useState(false);

    const handleSubmit = async () => {
        if (!motif.trim() || motif.trim().length < 5) {
            toast.error('Le motif doit contenir au moins 5 caractères');
            return;
        }

        try {
            setLoading(true);
            await deletionRequestsAPI.create(prospect.id, motif.trim());
            toast.success('Demande de suppression envoyée aux administrateurs');
            setOpen(false);
            setMotif('');
            if (onSuccess) onSuccess();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erreur lors de la demande');
        } finally {
            setLoading(false);
        }
    };

    const prospectName = prospect.company_name || `${prospect.first_name || ''} ${prospect.last_name || ''}`.trim();

    return (
        <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
                <Button
                    variant="outline"
                    size="sm"
                    className="text-red-600 border-red-300 hover:bg-red-50"
                    data-testid="request-deletion-btn"
                >
                    <Trash2 className="w-4 h-4 mr-2" />
                    Demander la suppression
                </Button>
            </DialogTrigger>
            <DialogContent>
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <AlertTriangle className="w-5 h-5 text-amber-500" />
                        Demander la suppression
                    </DialogTitle>
                    <DialogDescription>
                        Vous demandez la suppression du dossier : <strong>{prospectName}</strong>
                    </DialogDescription>
                </DialogHeader>
                <div className="py-4 space-y-4">
                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-amber-800 text-sm">
                        <p>Cette demande sera envoyée aux administrateurs pour validation.</p>
                        <p className="mt-1">La suppression définitive nécessite leur approbation.</p>
                    </div>
                    <div className="space-y-2">
                        <Label htmlFor="motif">Motif de la demande *</Label>
                        <Input
                            id="motif"
                            placeholder="Pourquoi souhaitez-vous supprimer ce dossier ?"
                            value={motif}
                            onChange={(e) => setMotif(e.target.value)}
                            data-testid="deletion-motif-input"
                        />
                        <p className="text-xs text-slate-500">Minimum 5 caractères</p>
                    </div>
                </div>
                <DialogFooter className="gap-2">
                    <Button
                        variant="outline"
                        onClick={() => {
                            setOpen(false);
                            setMotif('');
                        }}
                        disabled={loading}
                    >
                        Annuler
                    </Button>
                    <Button
                        variant="destructive"
                        onClick={handleSubmit}
                        disabled={loading || motif.trim().length < 5}
                        data-testid="submit-deletion-request-btn"
                    >
                        {loading ? 'Envoi...' : 'Envoyer la demande'}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

export default RequestDeletionButton;
